import { BUILD_COMMIT, UNKNOWN_COMMIT } from "./buildInfo";

/**
 * Noticing a deploy before it breaks something.
 *
 * `staleDeploy.ts` is the recovery: it reacts once a chunk has already gone
 * missing. This is the other half — asking the Worker what it is serving and
 * offering a reload while the person is still on a page that works, rather than
 * at the moment they click something and it fails.
 *
 * `/health` carries the deployed commit (apps/api/src/app.ts) and Vite inlines
 * the bundle's own commit, and the two are the same value by construction: the
 * deploy workflow passes `github.sha` to the Worker and the frontend build reads
 * `GITHUB_SHA`.
 */

/** How often to ask, on top of asking whenever the tab is brought forward. */
export const VERSION_POLL_MS = 5 * 60 * 1000;

function usable(value: unknown): value is string {
  return (
    typeof value === "string" && value.length > 0 && value !== UNKNOWN_COMMIT
  );
}

/**
 * Whether the Worker is serving a different build than this bundle came from.
 *
 * Anything short of a confident "these two differ" is `false`. A missing or
 * unknown value on either side means we cannot tell, and "cannot tell" must not
 * surface as a reload prompt — that would put one in front of every developer,
 * on every page, forever, since local builds have no deployed version to
 * compare against.
 */
export function isNewVersionAvailable(
  running: string | undefined,
  deployed: unknown,
): boolean {
  if (!usable(running) || !usable(deployed)) return false;
  return running !== deployed;
}

/**
 * Ask the Worker which build it is serving. Resolves to `undefined` whenever
 * the answer isn't a usable version — offline, a 500, or the dev server
 * answering `/health` with the SPA's HTML.
 *
 * Every failure is silent on purpose. This runs on a timer behind whatever the
 * person is actually doing, and a background poll has no business raising
 * anything at them.
 */
export async function fetchDeployedVersion(
  signal?: AbortSignal,
): Promise<string | undefined> {
  try {
    const response = await fetch("/health", {
      signal,
      headers: { Accept: "application/json" },
      // The point is to observe a change, so a cached answer is worse than none.
      cache: "no-store",
    });
    if (!response.ok) return undefined;

    const body: unknown = await response.json();
    const version =
      typeof body === "object" && body !== null
        ? (body as { version?: unknown }).version
        : undefined;
    return usable(version) ? version : undefined;
  } catch {
    return undefined;
  }
}

/** The build this bundle was compiled from, as the check sees it. */
export const RUNNING_COMMIT = BUILD_COMMIT;
